import { useEffect } from 'react';
import {
  BOUGHT_AS_HINT,
  CURRENT_LABEL,
  DONE_LABEL,
  OVERRIDE_BOUGHT_AS_LABEL,
  RESET_BOUGHT_AS_LABEL,
  USE_THIS_LABEL,
  type SoldAsOption,
} from '@/lib/designations/rowOverrides';
import './variantMappingDesign.css';

interface BoughtAsOverrideDialogProps {
  open: boolean;
  contextLabel?: string;
  options: SoldAsOption[];
  resetEnabled: boolean;
  pending?: boolean;
  onUseThis: (option: SoldAsOption) => void;
  onReset: () => void;
  onDone: () => void;
}

const BoughtAsOverrideDialog = ({
  open,
  contextLabel,
  options,
  resetEnabled,
  pending = false,
  onUseThis,
  onReset,
  onDone,
}: BoughtAsOverrideDialogProps) => {
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      onDone();
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [open, onDone]);

  if (!open) return null;

  return (
    <div data-testid="bought-as-override-dialog" className="vm-design__picker">
      <div className="vm-design__picker-head">
        <div className="vm-design__picker-head-copy">
          <div className="vm-design__picker-title" role="heading">{OVERRIDE_BOUGHT_AS_LABEL}</div>
          <div className="vm-design__picker-hint">
            {contextLabel ? `${contextLabel} · ` : ''}{BOUGHT_AS_HINT}
          </div>
        </div>
        <button
          type="button"
          className="vm-design__assoc-close"
          aria-label="Close"
          onClick={onDone}
        >
          ×
        </button>
      </div>
      <ul className="vm-design__picker-list">
        {options.map((option) => (
          <li
            key={option.columnKey}
            className={`vm-design__picker-row${option.current ? ' vm-design__picker-row--current' : ''}`}
            data-testid={`bought-as-option-${option.columnKey}`}
          >
            <div className="min-w-0 flex-1">
              <div className="vm-design__picker-value">{option.value}</div>
              <div className="vm-design__picker-type">{option.columnLabel}</div>
            </div>
            {option.current ? (
              <span className="vm-design__picker-btn vm-design__picker-btn--bought" data-testid={`bought-as-current-${option.columnKey}`}>
                {CURRENT_LABEL}
              </span>
            ) : (
              <button type="button" className="vm-design__picker-btn vm-design__picker-btn--idle" disabled={pending} onClick={() => onUseThis(option)}>
                {USE_THIS_LABEL}
              </button>
            )}
          </li>
        ))}
      </ul>
      <div className="vm-design__picker-foot">
        <button type="button" className="vm-design__picker-reset" disabled={!resetEnabled || pending} onClick={onReset} data-testid="bought-as-reset">
          {RESET_BOUGHT_AS_LABEL}
        </button>
        <button type="button" className="vm-design__picker-done" onClick={onDone} data-testid="bought-as-done">
          {DONE_LABEL}
        </button>
      </div>
    </div>
  );
};

export default BoughtAsOverrideDialog;
